import React, { useState, useContext, useEffect } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { Header } from '@/components/molecules/front-tablet/header'
import { Footer } from '@/components/molecules/front-tablet/footer'
import * as api from '@/apis/aipass'
import { SearchCheckOutReservation } from '@/models/reservation'
import { LoaderContextCreator } from '@/contexts/loader'
import { useSelfCheckOutState } from '@/hooks/use-self-check-out-state'
import { FrontTabletLayout } from '@/components/layouts/front-tablet-layout'
import { formSchema, FormType, ReservationSearchForm } from '@/components/molecules/front-tablet/reservation-search-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, useWatch } from 'react-hook-form'
import { FrontTabletInputField } from '@/components/molecules/front-tablet/input/input-field'
import { Body } from '@/components/molecules/front-tablet/body'
import { SearchItem, SearchSetting } from '@/models/search-setting'
import { useSelfCheckoutInitialization } from '@/hooks/use-self-checkout-initialization'
import { reportError } from '@/components/apps/error-boundary'
import { notification } from 'antd'

export const SearchReservation: React.FC<{}> = () => {
  const { t } = useTranslation()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const history = useHistory()
  const { state, saveState } = useSelfCheckOutState()
  const formReturn = useForm<FormType>({
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: state.searchReservation?.condition,
  })
  const watchSearchMode = useWatch({ control: formReturn.control, name: 'mode' })

  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [isDuplicateReservationBySameName, setIsDuplicateReservationBySameName] = useState(false)
  const [searchItems, setSearchItems] = useState<SearchSetting[]>([])

  const { isInitialized, initializedState } = useSelfCheckoutInitialization({
    onInitialized: async () => {
      const _searchItems = await api.fetchTabletCheckoutSettings(state.hotelId)
      setSearchItems(_searchItems)
    },
  })

  const hasDuplicateReservationBySameName = (searchedReservations: SearchCheckOutReservation[]): boolean => {
    return searchedReservations.some((searchedReservation, _, reservations) => {
      return reservations.some(
        reservation =>
          (reservation.guestName === searchedReservation.guestName ||
            reservation.reservationUserName === searchedReservation.reservationUserName ||
            reservation.guestNameKana === searchedReservation.guestNameKana ||
            reservation.reservationUserNameKana === searchedReservation.reservationUserNameKana) &&
          reservation.guestTel !== searchedReservation.guestTel,
      )
    })
  }

  const submit = async (formValues: FormType) => {
    if (watchSearchMode === 'qr') {
      saveState({
        ...state,
        searchReservation: {
          condition: formValues,
          result: { reservations: [] },
        },
      })
      history.push({ pathname: '/self-checkout/qr-camera' })
      return
    }

    setIsLoading(true)
    let stayReservations: SearchCheckOutReservation[] = []
    try {
      // APIには、選択されたmodeの値しか送らない
      const res = await api.fetchSelfCheckOutReservations({
        hotelId: state.hotelId,
        telephone: formValues.mode === SearchItem.Telephone ? formValues.tel : undefined,
        reservationId: formValues.mode === SearchItem.ReservationId ? formValues.reservationId : undefined,
        givenName: formValues.mode === SearchItem.GuestName ? formValues.firstName : undefined,
        familyName: formValues.mode === SearchItem.GuestName ? formValues.lastName : undefined,
        givenNameKana: formValues.mode === SearchItem.GuestNameKana ? formValues.firstNameKana : undefined,
        familyNameKana: formValues.mode === SearchItem.GuestNameKana ? formValues.lastNameKana : undefined,
        keyNumber: formValues.mode === SearchItem.RoomKey ? formValues.keyNumber : undefined,
        roomNumber: formValues.mode === SearchItem.RoomKey ? formValues.roomNumber : undefined,
      })
      stayReservations = res.stayReservations
    } catch (e) {
      reportError(e)
    } finally {
      setIsLoading(false)
    }
    if (stayReservations.length === 0) {
      notification.warn({
        message: t('Reservation not found. Please try a different search method.'),
        style: { whiteSpace: 'pre-line' },
      })
      return
    }

    if (watchSearchMode !== 'telephone' && hasDuplicateReservationBySameName(stayReservations)) {
      setIsDuplicateReservationBySameName(true)
      return
    }

    saveState({
      ...state,
      searchReservation: {
        condition: formValues,
        result: {
          reservations: stayReservations,
        },
      },
    })
    history.push({ pathname: '/self-checkout/select-reservation' })
  }

  useEffect(() => {
    if (initializedState && !state.searchReservation) {
      saveState(initializedState)
    }
  }, [initializedState])

  if (!isInitialized) {
    return <></>
  }
  return (
    <FrontTabletLayout state={state}>
      <Header goToPreviousPage={history.goBack} title={t('Search your reservation')} />
      <Body
        subtitle={t(
          isDuplicateReservationBySameName
            ? 'Found a reservation with the same name.\nPlease enter your phone number.'
            : 'Please search by one of the following methods',
        )}
      >
        <div css={form}>
          {isDuplicateReservationBySameName ? (
            <div className="duplicate-form-block">
              <p className="field-title">{t('Phone number')}</p>
              <div className="text-field">
                <FrontTabletInputField
                  name="phoneNumber"
                  value={phoneNumber}
                  onChange={e => setPhoneNumber(e.target.value)}
                  type="tel"
                  placeholder="08012345678"
                />
                <input />
              </div>
            </div>
          ) : (
            <ReservationSearchForm formReturn={formReturn} searchItems={searchItems} />
          )}
        </div>
      </Body>
      <Footer
        right={{
          isDisabled: !formReturn.formState.isValid,
          onClick: formReturn.handleSubmit(submit),
          label: t(watchSearchMode === 'qr' ? 'Next' : 'Search'),
        }}
      />
    </FrontTabletLayout>
  )
}

const form = css({
  backgroundColor: 'white',
  borderRadius: '10px',
  '.duplicate-form-block': {
    '.field-title': { fontWeight: 'bold', fontSize: 20 },
    '.text-field': { marginTop: 16, marginLeft: 43 },
  },
})
